import React, { type ReactNode, useMemo } from 'react';

import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { ConfluenceIcon } from '@atlaskit/logo';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { getLikeCount } from '../../../../../../../common/ui/like-count';
import SearchMetadataLink from '../../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import type { searchGraphConfluenceDc_AtlassianSearchPage$key } from './__generated__/searchGraphConfluenceDc_AtlassianSearchPage.graphql';
import { messages } from './messages';

const SUBTYPE_PAGE = 'page';
const SUBTYPE_BLOGPOST = 'blogpost';
const LIKE_REACTION_TYPE = 'like';

const CONFLUENCE_DC_LABELS: Record<string, MessageDescriptor> = {
	[SUBTYPE_PAGE]: messages.pageLabel,
	[SUBTYPE_BLOGPOST]: messages.blogpostLabel,
};

const ATTACHMENT_SUBTYPES = new Set([
	'archive',
	'audio',
	'code',
	'document',
	'image',
	'pdf',
	'presentation',
	'spreadsheet',
	'video',
	'web-page',
	'other',
]);

const getLabel = (subtype: string): MessageDescriptor => {
	if (ATTACHMENT_SUBTYPES.has(subtype)) {
		return messages.attachmentLabel;
	}
	return CONFLUENCE_DC_LABELS[subtype];
};

export const SearchGraphConfluenceDc = ({
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	selectedProduct,
	thirdPartyConfigs,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphResultProps<searchGraphConfluenceDc_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphConfluenceDc_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				subtype
				title
				url
				lastModifiedDate
				excerpt
				entity {
					... on ExternalDocument {
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
							}
						}
						reactions {
							reactionType
							total
						}
					}
				}
			}
		`,
		node,
	);

	const { id, lastModifiedDate, url, title, excerpt, subtype, entity } = data;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const label = subtype ? <FormattedMessage {...getLabel(subtype)} /> : null;
	const site = thirdPartyConfigs?.[ProductKeys3P.ConfluenceDC]?.workspaceName;
	const spaceName = entity?.container?.displayName;
	const spaceUrl = entity?.container?.url;
	const space =
		spaceUrl && spaceName ? <SearchMetadataLink label={spaceName} href={spaceUrl} /> : spaceName;

	const metadataParts: [string, ReactNode][] = [
		['label', label],
		['site', site],
		['space', space],
		['lastModified', lastModifiedComponent],
	];

	const numberOfLikes =
		data.entity?.reactions?.find(
			(reaction) => reaction?.reactionType?.toLowerCase() === LIKE_REACTION_TYPE,
		)?.total ?? 0;

	const likeCount = useMemo(() => getLikeCount(numberOfLikes), [numberOfLikes]);

	return (
		<SearchResult
			productConfigs={productConfigs}
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={<ConfluenceIcon size="medium" />}
			headerMetadataParts={metadataParts}
			headerMetadataSocialParts={[['like-count', likeCount]]}
			title={enrichExcerpt(title)}
			url={url}
			description={<DescriptionBody description={processExcerpt(excerpt)} />}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};
